// https://github.com/sygeman/yandex-map/blob/main/providers/map-provider.tsx

'use client';

import { search, suggest } from '@yandex/ymaps3-types';
import { ReactifiedModule } from '@yandex/ymaps3-types/reactify';
import Script from 'next/script';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

export type ReactifyApi = ReactifiedModule<typeof import('@yandex/ymaps3-types')>;
export type Ymaps3Controls = ReactifiedModule<
  typeof import('@yandex/ymaps3-types/packages/controls')
>;
export type Ymaps3Clusterer = ReactifiedModule<
  typeof import('@yandex/ymaps3-types/packages/clusterer')
>;

type MapsContextValue = {
  reactifyApi: ReactifyApi | null;
  controls: Ymaps3Controls | null;
  clusterer: Ymaps3Clusterer | null;
  suggest: typeof suggest | null;
  search: typeof search | null;
  loading: boolean;
};

const initialState: MapsContextValue = {
  reactifyApi: null,
  controls: null,
  clusterer: null,
  suggest: null,
  search: null,
  loading: false
};

const MapsContext = createContext<MapsContextValue>(initialState);

interface MapsProviderProps {
  children?: React.ReactNode;
  apiUrl: string | undefined;
}

export function MapsProvider({ children, apiUrl }: MapsProviderProps) {
  const [state, setState] = useState<MapsContextValue>(initialState);

  const contextValue = useMemo(() => state, [state]);

  const onLoad = async () => {
    setState((s) => ({ ...s, loading: true }));
    const [ymaps3React, ymaps3Controls, ymaps3Clusterer] = await Promise.all([
      ymaps3.import('@yandex/ymaps3-reactify'),
      ymaps3.import('@yandex/ymaps3-controls@0.0.1'),
      ymaps3.import('@yandex/ymaps3-clusterer@0.0.1'),
      ymaps3.ready
    ]);

    const reactify = ymaps3React.reactify.bindTo(React, ReactDOM);

    setState({
      reactifyApi: reactify.module(ymaps3),
      controls: reactify.module(ymaps3Controls),
      clusterer: reactify.module(ymaps3Clusterer),
      suggest: ymaps3.suggest,
      search: ymaps3.search,
      loading: false
    });
  };

  useEffect(() => {
    if (
      typeof ymaps3 !== 'undefined' &&
      contextValue.reactifyApi === null &&
      !contextValue.loading
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      onLoad();
    }
  }, [contextValue]);

  return (
    <MapsContext.Provider value={contextValue}>
      <Script
        src={apiUrl}
        onLoad={onLoad}
        // eslint-disable-next-line no-console
        onError={console.error}
      />
      {children}
    </MapsContext.Provider>
  );
}

export const useMaps = () => useContext(MapsContext);
